import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock,
  RefreshCw,
  Eye,
  Calendar,
  Layers,
  Building2,
  ShieldCheck,
  Plus,
  Zap,
} from 'lucide-react';
import { realtimeDbService as dbService } from '../../services/realtimeDbService';
import { AlertDialog } from '../common/AlertDialog';
import {
  AIVisibilitySubscription,
  AIVisibilityProfile,
  AIVisibilityTask,
  SiteData,
  UserProfile,
} from '../../types';

interface AdminAIVisibilityManagementProps {
  sites?: SiteData[];
  users?: UserProfile[];
}

export const AdminAIVisibilityManagement: React.FC<AdminAIVisibilityManagementProps> = ({
  sites = [],
  users = [],
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [subscriptions, setSubscriptions] = useState<AIVisibilitySubscription[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<AIVisibilityProfile | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<AIVisibilityTask[]>([]);
  const [extendDialog, setExtendDialog] = useState<{ open: boolean; sub: AIVisibilitySubscription | null }>({
    open: false,
    sub: null,
  });
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Manual Activation Form State
  const [isManualModalOpen, setIsManualModalOpen] = useState<boolean>(false);
  const [manualTenantId, setManualTenantId] = useState<string>('');
  const [manualSiteId, setManualSiteId] = useState<string>('');
  const [manualDays, setManualDays] = useState<number>(365);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await dbService.getAllAIVisibilitySubscriptions();
      setSubscriptions(data);
    } catch (err) {
      console.error('Error loading AI visibility subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const handleInspectTenant = async (tenantId: string) => {
    setSelectedTenantId(tenantId);
    try {
      const [prof, tsks] = await Promise.all([
        dbService.getAIVisibilityProfile(tenantId),
        dbService.getAIVisibilityTasks(tenantId),
      ]);
      setSelectedProfile(prof);
      setSelectedTasks(tsks);
    } catch (err) {
      console.error('Error inspecting tenant AI visibility:', err);
    }
  };

  const handleExtendSubscription = (sub: AIVisibilitySubscription) => {
    setExtendDialog({ open: true, sub });
  };

  const handleExecuteExtendSubscription = async () => {
    if (!extendDialog.sub) return;
    const sub = extendDialog.sub;
    setActionLoading(true);
    try {
      await dbService.activateAIVisibilitySubscription({
        tenantId: sub.tenantId,
        userId: sub.userId,
        price: 5000,
        currency: 'TRY',
        paymentId: `ADMIN_MANUAL_${Date.now()}`,
        orderId: `MANUAL_${Date.now()}`,
        siteId: sub.websiteId,
        validityDays: 365,
      });
      setActionSuccess('Abonelik başarıyla 1 yıl uzatıldı.');
      setTimeout(() => setActionSuccess(null), 3000);
      await loadSubscriptions();
      setExtendDialog({ open: false, sub: null });
    } catch (err) {
      console.error('Error extending subscription:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleManualActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualTenantId.trim()) return;

    setActionLoading(true);
    try {
      await dbService.activateAIVisibilitySubscription({
        tenantId: manualTenantId.trim(),
        userId: manualTenantId.trim(),
        price: 5000,
        currency: 'TRY',
        paymentId: `ADMIN_MANUAL_${Date.now()}`,
        orderId: `MANUAL_ADMIN_${Date.now()}`,
        siteId: manualSiteId || undefined,
        validityDays: manualDays || 365,
      });

      setIsManualModalOpen(false);
      setManualTenantId('');
      setManualSiteId('');
      setActionSuccess('AI Arama Görünürlüğü aboneliği manuel olarak aktif edildi.');
      setTimeout(() => setActionSuccess(null), 3000);
      await loadSubscriptions();
    } catch (err) {
      console.error('Manual activation error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const now = Date.now();
  const activeSubs = subscriptions.filter((s) => s.status === 'active' && s.endDate > now);
  const totalAnnualRevenue = activeSubs.reduce((acc, curr) => acc + (curr.priceTRY || 5000), 0);
  const expiringSoon = activeSubs.filter((s) => s.endDate - now <= 30 * 24 * 60 * 60 * 1000).length;

  const filteredSubs = subscriptions.filter((s) => {
    const q = searchQuery.toLowerCase();
    return (
      s.tenantId.toLowerCase().includes(q) ||
      (s.orderId && s.orderId.toLowerCase().includes(q)) ||
      (s.paymentId && s.paymentId.toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-6 text-slate-100">
      {/* Header and Quick Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            <span>AI Arama Görünürlüğü (Super Admin)</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Müşterilerin ChatGPT Search, Gemini ve yapay zeka arama motorları varlık optimizasyonu abonelikleri.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={loadSubscriptions}
            className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold flex items-center gap-2 transition cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Yenile</span>
          </button>
          <button
            type="button"
            onClick={() => setIsManualModalOpen(true)}
            className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold flex items-center gap-2 transition shadow-lg shadow-indigo-600/30 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Manuel Abonelik Tanımla</span>
          </button>
        </div>
      </div>

      {actionSuccess && (
        <div className="p-3 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span>{actionSuccess}</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-slate-900/80 p-5 rounded-2xl border border-slate-800/80 space-y-1">
          <span className="text-xs text-slate-400 font-medium">Toplam Aktif AI Aboneliği</span>
          <div className="text-2xl font-black text-white">{activeSubs.length}</div>
          <p className="text-[11px] text-slate-500">5.000 TL / Yıl Yıllık Plan</p>
        </div>

        <div className="bg-slate-900/80 p-5 rounded-2xl border border-slate-800/80 space-y-1">
          <span className="text-xs text-slate-400 font-medium">Yıllık Toplam Gelir</span>
          <div className="text-2xl font-black text-emerald-400">
            {totalAnnualRevenue.toLocaleString('tr-TR')} TL
          </div>
          <p className="text-[11px] text-slate-500">Doğrulanmış Shopier ve yetkili işlemler</p>
        </div>

        <div className="bg-slate-900/80 p-5 rounded-2xl border border-slate-800/80 space-y-1">
          <span className="text-xs text-slate-400 font-medium">30 Gün İçinde Bitiş</span>
          <div className="text-2xl font-black text-amber-400">{expiringSoon}</div>
          <p className="text-[11px] text-slate-500">Yenileme uyarısı gönderilebilir</p>
        </div>
      </div>

      {/* Subscriptions List */}
      <div className="bg-slate-900/80 rounded-2xl border border-slate-800/80 p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Tenant ID veya sipariş ara..."
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-200 placeholder-slate-500 focus:outline-hidden focus:border-indigo-500"
            />
          </div>
          <span className="text-xs text-slate-400">
            Toplam {filteredSubs.length} Kayıt
          </span>
        </div>

        {filteredSubs.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-xs">
            Kayıtlı AI Arama Görünürlüğü aboneliği bulunamadı.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 uppercase tracking-wider text-[10px]">
                  <th className="pb-3 font-semibold">Tenant / Müşteri</th>
                  <th className="pb-3 font-semibold">Durum</th>
                  <th className="pb-3 font-semibold">Başlangıç</th>
                  <th className="pb-3 font-semibold">Bitiş Tarihi</th>
                  <th className="pb-3 font-semibold">Fiyat</th>
                  <th className="pb-3 font-semibold">Ödeme ID</th>
                  <th className="pb-3 font-semibold text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {filteredSubs.map((sub) => {
                  const isActive = sub.status === 'active' && sub.endDate > now;
                  const tenantUser = users.find((u) => u.uid === sub.tenantId);
                  const tenantSite = sites.find((s) => s.ownerId === sub.tenantId || s.id === sub.websiteId);

                  return (
                    <tr key={sub.id} className="hover:bg-slate-800/40 transition">
                      <td className="py-3.5">
                        <div className="font-semibold text-white">
                          {tenantUser?.displayName || tenantSite?.name || sub.tenantId}
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono">
                          {sub.tenantId}
                        </div>
                      </td>
                      <td className="py-3.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          isActive
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                        }`}>
                          {isActive ? 'Aktif' : 'Süresi Dolmuş'}
                        </span>
                      </td>
                      <td className="py-3.5 text-slate-300">
                        {new Date(sub.startDate).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="py-3.5 text-slate-300">
                        {new Date(sub.endDate).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="py-3.5 font-bold text-emerald-400">
                        {sub.priceTRY.toLocaleString('tr-TR')} TL
                      </td>
                      <td className="py-3.5 text-slate-400 font-mono text-[10px]">
                        {sub.paymentId || sub.orderId || '-'}
                      </td>
                      <td className="py-3.5 text-right space-x-2">
                        <button
                          type="button"
                          onClick={() => handleInspectTenant(sub.tenantId)}
                          className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-medium transition cursor-pointer"
                        >
                          İncele
                        </button>
                        <button
                          type="button"
                          onClick={() => handleExtendSubscription(sub)}
                          disabled={actionLoading}
                          className="px-2.5 py-1 rounded-lg bg-indigo-600/80 hover:bg-indigo-600 text-white text-[11px] font-medium transition cursor-pointer disabled:opacity-50"
                        >
                          +1 Yıl Uzat
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Selected Tenant Detailed Profile Drawer / Card */}
      {selectedTenantId && selectedProfile && (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="font-bold text-white text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4 text-indigo-400" />
              <span>Tenant Varlık Profili: {selectedProfile.businessName}</span>
            </h3>
            <button
              type="button"
              onClick={() => setSelectedTenantId(null)}
              className="text-xs text-slate-400 hover:text-white cursor-pointer"
            >
              Kapat
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-slate-400 block text-[10px]">Yasal Unvan</span>
              <strong className="text-slate-200">{selectedProfile.legalName || '-'}</strong>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px]">Web Sitesi</span>
              <strong className="text-slate-200">{selectedProfile.website || '-'}</strong>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px]">Konum / Şehir</span>
              <strong className="text-slate-200">{selectedProfile.district ? `${selectedProfile.district} / ` : ''}{selectedProfile.city}</strong>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px]">Telefon</span>
              <strong className="text-slate-200">{selectedProfile.phone || '-'}</strong>
            </div>
          </div>

          <div className="pt-2">
            <span className="text-slate-400 block text-[10px] mb-1">Kayıtlı Hizmetler ({selectedProfile.services?.length || 0})</span>
            <div className="flex flex-wrap gap-1.5">
              {(selectedProfile.services || []).map((srv, idx) => (
                <span key={idx} className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[11px]">
                  {srv}
                </span>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <span className="text-slate-400 block text-[10px] mb-1">Operasyonel Görevler ({selectedTasks.length})</span>
            <div className="space-y-1.5">
              {selectedTasks.map((tsk) => (
                <div key={tsk.taskId} className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex justify-between items-center text-xs">
                  <span className="text-slate-300">{tsk.title}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                    tsk.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-blue-500/20 text-blue-300'
                  }`}>
                    {tsk.status === 'completed' ? 'Tamamlandı' : 'Devam Ediyor'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Manual Subscription Creation Modal */}
      {isManualModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-md w-full space-y-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-400" />
              <span>Manuel AI Görünürlük Tanımla</span>
            </h3>

            <form onSubmit={handleManualActivate} className="space-y-4 text-xs">
              <div className="space-y-1">
                <label className="text-slate-300 font-semibold">Tenant ID (Kullanıcı UID)</label>
                <input
                  type="text"
                  required
                  value={manualTenantId}
                  onChange={(e) => setManualTenantId(e.target.value)}
                  placeholder="Firebase User UID"
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-hidden focus:border-indigo-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-slate-300 font-semibold">Website ID (İsteğe Bağlı)</label>
                <input
                  type="text"
                  value={manualSiteId}
                  onChange={(e) => setManualSiteId(e.target.value)}
                  placeholder="Site ID"
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-hidden focus:border-indigo-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-slate-300 font-semibold">Geçerlilik Süresi (Gün)</label>
                <input
                  type="number"
                  value={manualDays}
                  onChange={(e) => setManualDays(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-hidden focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsManualModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold cursor-pointer disabled:opacity-50"
                >
                  {actionLoading ? 'Kaydediliyor...' : 'Aboneliği Başlat'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Extend Subscription AlertDialog */}
      <AlertDialog
        open={extendDialog.open}
        title="Aboneliği 1 Yıl Uzat?"
        description={`${extendDialog.sub?.tenantId || 'Seçili işletme'} için AI Görünürlük aboneliğini 1 yıl (365 gün) uzatmak istediğinize emin misiniz?`}
        variant="info"
        confirmText={actionLoading ? 'Uzatılıyor...' : 'Evet, Uzat'}
        cancelText="Vazgeç"
        loading={actionLoading}
        onConfirm={handleExecuteExtendSubscription}
        onCancel={() => {
          if (!actionLoading) setExtendDialog({ open: false, sub: null });
        }}
      />
    </div>
  );
};
